// Grouping of the match-detail statistics (Marin work order 2026-08-14):
// render EVERYTHING the merged match-history endpoint delivers, grouped, and
// hide what a given match does not have — no dash graveyards.
//
// Source of truth for the available keys is the BE payload builder
// `_build_statistics_dict` (dashboard/views.py) over TSPlayerMatchStatistics.
// Matches/Lineup build (brief §1, 2026-08-17): the previously-missing provider
// fields (shots_ibox/obox, big_chance_created/missed, tackles_succ,
// aerial_won/lost, ground_won/lost) are added ADDITIVELY to model + payload by
// the BE part of this build; the rows below consume them verbatim. Historical
// rows carry NULL there, which simply hides the row (empty rule).
//
// Row kinds:
//   value  -> plain "label: n"
//   ratio  -> "succ/total (pct%)" with bar, the existing Long-Ball pattern
//             (a missing success count renders as 0 of total). With
//             `valueWhenNoSucc: true` the row degrades to a PLAIN VALUE row
//             instead when the success count was never delivered — used for
//             Tackles, where tackles_succ is new and "0/6 (0%)" on a
//             historical row would be a lie, not a zero.
//   pair   -> "won/(won+lost) (pct%)" with bar, computed from a won/lost
//             field pair. Optional fallback pair: NEW FIELDS WIN — the
//             fallback keys are only consulted when BOTH new keys are absent
//             (brief §1: never show old duel_won/duel_lost alongside).
//   card   -> yellow/red card chip styling (Discipline)

export const hasStat = (v) =>
  v !== null && v !== undefined && !Number.isNaN(Number(v));

// For a "pair" row: which won/lost key pair actually carries data. New keys
// win as soon as EITHER of them is present; the legacy fallback pair is only
// consulted when both new keys are absent. Returns null when neither pair has
// data (row renders nothing).
export const pairSource = (row, stats) => {
  if (!stats) return null;
  if (hasStat(stats[row.wonKey]) || hasStat(stats[row.lostKey])) {
    return { wonKey: row.wonKey, lostKey: row.lostKey };
  }
  if (
    row.fallbackWonKey &&
    (hasStat(stats[row.fallbackWonKey]) || hasStat(stats[row.fallbackLostKey]))
  ) {
    return { wonKey: row.fallbackWonKey, lostKey: row.fallbackLostKey };
  }
  return null;
};

// A ratio row renders when the TOTAL is present; a missing success count
// renders as 0 of total. A value row renders only when its value is present.
export const STAT_GROUPS = [
  {
    id: "offensive",
    title: "Offensive",
    rows: [
      { kind: "value", label: "Assists", key: "assists" },
      { kind: "value", label: "Penalties scored", key: "penalty" },
      { kind: "ratio", label: "Dribbles", succKey: "dribble_succ", totalKey: "dribble", color: "#2283FF" },
      { kind: "value", label: "Hit woodwork", key: "hit_woodwork" },
      { kind: "value", label: "Fast breaks", key: "fastbreaks" },
      { kind: "value", label: "Fast break shots", key: "fastbreak_shots" },
      { kind: "value", label: "Fast break goals", key: "fastbreak_goals" },
      { kind: "value", label: "Free kicks", key: "freekicks" },
      { kind: "value", label: "Freekick goals", key: "freekick_goals" },
    ],
  },
  {
    id: "defensive",
    title: "Defensive",
    rows: [
      // Brief §1: the single Tackles row becomes the tackles_succ/tackles
      // ratio row. valueWhenNoSucc keeps historical rows (no tackles_succ in
      // the DB yet) as the plain value they always were.
      { kind: "ratio", label: "Tackles", succKey: "tackles_succ", totalKey: "tackles", color: "#2283FF", valueWhenNoSucc: true },
      { kind: "value", label: "Interceptions", key: "interceptions" },
      { kind: "value", label: "Clearances", key: "clearances" },
      { kind: "value", label: "Blocked shots", key: "blocked_shots" },
    ],
  },
  {
    id: "duels",
    title: "Duels",
    rows: [
      { kind: "ratio", label: "Duels won", succKey: "duels_won", totalKey: "duels", color: "#38AB75" },
      // Brief §1: aerial + ground splits as won vs (won+lost) under the total
      // duels row. New fields win; legacy duel_won/duel_lost is the aerial
      // fallback ONLY when both aerial keys are absent — never both.
      { kind: "pair", label: "Aerial duels won", wonKey: "aerial_won", lostKey: "aerial_lost", fallbackWonKey: "duel_won", fallbackLostKey: "duel_lost", color: "#2283FF" },
      { kind: "pair", label: "Ground duels won", wonKey: "ground_won", lostKey: "ground_lost", color: "#F2994A" },
      { kind: "value", label: "Dispossessed", key: "dispossessed" },
    ],
  },
  {
    id: "goalkeeping",
    title: "Goalkeeping",
    keeperOnly: true,
    rows: [
      { kind: "value", label: "Saves", key: "saves" },
      { kind: "value", label: "Punches", key: "punches" },
      { kind: "ratio", label: "Runs out", succKey: "runs_out_succ", totalKey: "runs_out", color: "#2283FF" },
      { kind: "value", label: "High claims", key: "good_high_claim" },
    ],
  },
];

// Extra rows merged into the existing Passing card (same ratio pattern).
export const PASSING_EXTRA_ROWS = [
  { kind: "ratio", label: "Crosses", succKey: "crosses_accuracy", totalKey: "crosses", color: "#38AB75" },
];

// Extra rows merged into the bespoke Shooting card (brief §1). Same
// single-value idiom as every other value row; absent fields render nothing.
export const SHOOTING_EXTRA_ROWS = [
  { kind: "value", label: "Shots inside box", key: "shots_ibox" },
  { kind: "value", label: "Shots outside box", key: "shots_obox" },
  { kind: "value", label: "Big chances created", key: "big_chance_created" },
  { kind: "value", label: "Big chances missed", key: "big_chance_missed" },
];

// Extra rows merged into the Discipline (formerly Fouls) card.
export const DISCIPLINE_EXTRA_ROWS = [
  { kind: "value", label: "Was fouled", key: "was_fouled" },
  { kind: "value", label: "Offsides", key: "offsides" },
  { kind: "value", label: "Second yellow cards", key: "yellow2red_cards" },
];

export const rowHasData = (row, stats) => {
  if (!stats) return false;
  if (row.kind === "pair") return pairSource(row, stats) !== null;
  if (row.kind === "ratio") return hasStat(stats[row.totalKey]);
  return hasStat(stats[row.key]);
};

export const groupHasData = (group, stats) =>
  group.rows.some((row) => rowHasData(row, stats));

// TheSports reports 0 (not null) for stats a player simply had none of. For
// GOALKEEPERS the attacking numbers are structurally zero, so an all-zero
// Offensive group is a zero graveyard, not information (Marin sharpening
// 2026-08-14) — it only renders when at least one value is non-zero. Field
// players keep their zeros: for them a 0 is a real match fact.
export const groupHasNonZeroData = (group, stats) =>
  group.rows.some((row) => {
    if (row.kind === "pair") {
      const source = pairSource(row, stats);
      if (!source) return false;
      return (
        (hasStat(stats[source.wonKey]) && Number(stats[source.wonKey]) !== 0) ||
        (hasStat(stats[source.lostKey]) && Number(stats[source.lostKey]) !== 0)
      );
    }
    if (row.kind === "ratio") {
      return (
        (hasStat(stats[row.totalKey]) && Number(stats[row.totalKey]) !== 0) ||
        (hasStat(stats[row.succKey]) && Number(stats[row.succKey]) !== 0)
      );
    }
    return hasStat(stats[row.key]) && Number(stats[row.key]) !== 0;
  });

// ---- per-match goalkeeper + presence (Matches/Lineup build, brief §1) ------

const GK_STAT_KEYS = ["saves", "punches", "runs_out", "runs_out_succ", "good_high_claim"];

// Brief §1 verbatim: the Goalkeeping card renders ONLY when the player kept
// goal IN THIS MATCH — position G in the lineup, or any GK field > 0. The
// season position deliberately plays no part here (a keeper match with every
// GK field at 0 and no lineup position therefore hides the card — spec rule).
export const keptGoalInMatch = (stats, lineupInfo) => {
  const position = lineupInfo?.formation_position;
  if (typeof position === "string" && position.trim().toUpperCase() === "G") {
    return true;
  }
  return GK_STAT_KEYS.some(
    (key) => hasStat(stats?.[key]) && Number(stats[key]) > 0
  );
};

// Identity/meta keys of _build_statistics_dict — everything else counts as
// evidence of actual match participation.
const NON_EVIDENCE_KEYS = new Set([
  "match_id",
  "player_id",
  "team_id",
  "team",
  "first",
  "minutes_played",
]);

// 0-minutes case (brief §1): minutes_played 0/absent -> not played. When
// minutes are ABSENT but other stats exist (older SportMonks rows without a
// minutes entry) the data wins and the match counts as played — collapsing
// rendered statistics behind a "Not played" line would destroy information.
// inSquad separates the two quiet lines: an existing lineup entry or any stat
// evidence (including an explicit minutes 0 row) means he was in the squad
// ("unused substitute"); nothing at all means "Not in squad".
export const matchPresence = (selectedMatch) => {
  const stats = selectedMatch?.statistics || {};
  const minutes = stats.minutes_played;
  const anyEvidence = Object.entries(stats).some(
    ([key, value]) => !NON_EVIDENCE_KEYS.has(key) && hasStat(value)
  );
  const played = hasStat(minutes) ? Number(minutes) > 0 : anyEvidence;
  const inSquad =
    !!selectedMatch?.lineup_info ||
    anyEvidence ||
    hasStat(minutes) ||
    hasStat(stats.first);
  return { played, inSquad };
};

// Same goalkeeper detection the season-stats tab uses (PlayerStats.js).
export const isGoalkeeperPlayer = (playerData) => {
  const position =
    playerData?.player?.role?.code2 ||
    playerData?.player?.role?.code3 ||
    playerData?.player?.role?.name;
  return position === "GK" || position === "GKP" || position === "Goalkeeper";
};
